import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from "react-query";
import * as api from "../api/EvacuationApi";
import { fetchAlarmButtonTypes } from "../api/AlarmApi";
import { 
  EvacuationPlan, 
  CreateEvacuationPlanInput, 
  AlarmButtonType 
} from "../types/evacuation";

interface ApiResponse<T> {
  data: T;
}

export const useEvacuationPlans = (): UseQueryResult<ApiResponse<EvacuationPlan[]>> => {
  return useQuery(["evacuationplans"], api.fetchEvacuationPlans, {
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
  });
};

export const useEvacuationPlanById = (id: string | number | undefined): UseQueryResult<ApiResponse<EvacuationPlan>> => {
  return useQuery(
    ["evacuationplan", id],
    () => api.fetchEvacuationPlanById(id!),
    {
      enabled: !!id,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
    }
  );
};

export const useEvacuationPlansByType = (type: string | undefined): UseQueryResult<ApiResponse<EvacuationPlan[]>> => {
  return useQuery(
    ["evacuationplans", "type", type],
    () => api.fetchEvacuationPlansByType(type!),
    {
      enabled: !!type,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
    }
  );
};

export const useEvacuationPlansByOrganisation = (organisationId: string | null): UseQueryResult<ApiResponse<EvacuationPlan[]>> => {
  return useQuery(
    ["evacuationplans", "organisation", organisationId],
    () => api.fetchEvacuationPlansByOrganisation(organisationId!),
    {
      enabled: !!organisationId,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
    }
  );
};

export const useAlarmButtonTypes = (): UseQueryResult<AlarmButtonType[]> => {
  return useQuery(["alarmButtonTypes"], async () => {
    const response = await fetchAlarmButtonTypes();
    return response.data.map((type: { key: string; label: string }) => ({
      value: type.key,
      label: type.label,
    }));
  }, {
    staleTime: 1000 * 60 * 60,
    cacheTime: 1000 * 60 * 120,
  });
};

interface CreateEvacuationPlanResponse {
  data?: {
    id?: number;
    raw?: {
      insertId?: number;
    };
  };
  id?: number;
  insertId?: number;
}

export const useCreateEvacuationPlan = (): UseMutationResult<
  CreateEvacuationPlanResponse,
  Error,
  CreateEvacuationPlanInput
> => {
  const queryClient = useQueryClient();

  return useMutation(api.createEvacuationPlan, {
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["evacuationplans", "organisation", variables.organisation_id]);
    },
  });
};

export const useUpdateEvacuationPlan = (): UseMutationResult<
  unknown,
  Error,
  { id: number; data: Partial<CreateEvacuationPlanInput> }
> => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, data }) => api.updateEvacuationPlan(id, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(["evacuationplan", variables.id]);
        if (variables.data.organisation_id) {
          queryClient.invalidateQueries(["evacuationplans", "organisation", variables.data.organisation_id]);
        }
      },
    }
  );
};

export const useDeleteEvacuationPlan = (): UseMutationResult<unknown, Error, number> => {
  const queryClient = useQueryClient();

  return useMutation(api.deleteEvacuationPlan, {
    onSuccess: () => {
      queryClient.invalidateQueries(["evacuationplans"]);
    },
  });
};
